import { useCallback, useEffect, useState } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  isReconnecting: boolean;
  lastDisconnected?: Date;
  lastConnected?: Date;
}

const RECONNECTION_TIMEOUT = 3000; // 3 seconds to determine if we're reconnecting

export const useNetworkStatus = () => {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isReconnecting: false,
  });

  const [reconnectionTimer, setReconnectionTimer] = useState<NodeJS.Timeout | null>(null);

  const handleOnline = useCallback(() => {
    if (reconnectionTimer) {
      clearTimeout(reconnectionTimer);
      setReconnectionTimer(null);
    }

    setStatus((prev) => ({
      ...prev,
      isOnline: true,
      isReconnecting: false,
      lastConnected: new Date(),
    }));
  }, [reconnectionTimer]);

  const handleOffline = useCallback(() => {
    setStatus((prev) => ({
      ...prev,
      isOnline: false,
      isReconnecting: true,
      lastDisconnected: new Date(),
    }));

    // Set a timer to stop showing reconnecting after timeout
    const timer = setTimeout(() => {
      setStatus((prev) => ({
        ...prev,
        isReconnecting: false,
      }));
    }, RECONNECTION_TIMEOUT);

    setReconnectionTimer(timer);
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (typeof document === 'undefined') return;

    // When tab becomes visible, check network status
    if (document.visibilityState === 'visible') {
      const isCurrentlyOnline = navigator.onLine;

      setStatus((prev) => {
        // If we detect we're online but our state says offline, trigger reconnection
        if (isCurrentlyOnline && !prev.isOnline) {
          return {
            ...prev,
            isOnline: true,
            isReconnecting: false,
            lastConnected: new Date(),
          };
        }

        // If we detect we're offline but our state says online, mark as disconnected
        if (!isCurrentlyOnline && prev.isOnline) {
          return {
            ...prev,
            isOnline: false,
            isReconnecting: true,
            lastDisconnected: new Date(),
          };
        }

        return prev;
      });

      // If we just came back online, clear any reconnection timer
      if (isCurrentlyOnline && reconnectionTimer) {
        clearTimeout(reconnectionTimer);
        setReconnectionTimer(null);
      }
    }
  }, [reconnectionTimer]);

  const handleFocus = useCallback(() => {
    // Similar to visibility change but for window focus
    handleVisibilityChange();
  }, [handleVisibilityChange]);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);

      if (reconnectionTimer) {
        clearTimeout(reconnectionTimer);
      }
    };
  }, [handleOnline, handleOffline, handleVisibilityChange, handleFocus, reconnectionTimer]);

  const forceCheck = useCallback(() => {
    if (typeof navigator === 'undefined') return;

    const isCurrentlyOnline = navigator.onLine;
    setStatus((prev) => ({
      ...prev,
      isOnline: isCurrentlyOnline,
      isReconnecting: false,
      ...(isCurrentlyOnline ? { lastConnected: new Date() } : { lastDisconnected: new Date() }),
    }));
  }, []);

  return {
    ...status,
    forceCheck,
  };
};
